<script setup lang="ts">

  const { locale, t } = useI18n()
  const runtimeConfig = useRuntimeConfig()

  const { data: countriesData } = await useFetch<{ data: Array<any> }>(`${useBaseUrl()}/destination-list`, {
    key: 'countries',
    headers: { "Content-Type": "application/json" },
    params: { 
      locale,
      limit: 1000,
    }
  })

  // Map data
  const countries = computed<Country[]>(() => {
    return countriesData.value?.data ?? []
  })

  // Navigation
  const navigate = (city: Destination) => {
    navigateTo(city?.url ?? '', {
      open: {
        target: '_blank'
      }
    })
  }

  // SEO
  const canonicalUrl = computed(() => {
    const baseUrl = runtimeConfig.public.BASE_URL || ''
    return `${baseUrl}/${locale.value}/destinations`
  })

  useSeoMeta({
    title: t('meta.byhours-destinations.title'),
    description: t('meta.byhours-destinations.description'),
    keywords: t('meta.byhours-destinations.keywords'),
    ogTitle: t('meta.byhours-destinations.title'),
    ogDescription: t('meta.byhours-destinations.description'),
    ogUrl: canonicalUrl
  })

  useHead({
    link: [
      { rel: 'canonical', href: canonicalUrl }
    ]
  })

  const filterValue = ref('')
  const filteredCountries = computed(() => {
    const normalize = (str: string) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Normaliza una cadena eliminando marcas diacríticas (acentos) usando la Forma de Normalización Unicode D (NFD)
    return countries.value?.filter((country: Country) => 
      normalize(country.name).toLowerCase().includes(normalize(filterValue.value).toLowerCase())
    )
  })

  const countryName = ref('')
  const currentCountry: Ref<Destination> = ref({} as Destination)
  const getCountryInfo = (country: Country) => {
    if(countryName.value === country.name) {
      countryName.value = ''
      currentCountry.value = {} as Destination
      return
    }
    countryName.value = country.name
    currentCountry.value = country
    isOpen.value = false
  }

  const countryId = computed(() => {
    return currentCountry.value.id
  })

  const { data: citiesData, status: citiesStatus } = await useFetch<{ data: Array<any> }>(`${useBaseUrl()}/destination-list`, {
    key: `cities-${countryId}`,
    headers: { "Content-Type": "application/json" },
    params: { 
      country: countryId,
      locale,
      limit: 1000,
    }
  })

  // Map data
  const mapCities = computed<Country[]>(() => {
    return citiesData.value?.data ?? []
  })

  // Bound map
  const latlonCities = computed(() => {
    return mapCities.value?.map((city: { location: string }) => city.location.split(',')) ?? []
  })

  const mapKey = computed(() => JSON.stringify(latlonCities.value))

  const links = computed(() => {
    return [
      {
        label: 'Home',
        to: `/${locale.value}`
      },
      {
        label: `${t('labels.destinos')}`,
      }
    ]
  })

  const isOpen = ref(false)
  watch(isOpen, (newValue) => {
    if(newValue){
      filterValue.value = ''
    }
  })
  
</script>

<template>
  <div class="max-w-screen-2xl container mx-auto px-5 relative h-fit">
    <!-- Breadcrumb -->
    <UBreadcrumb class="py-4 sm:py-5" :items="links" />

    <div class="flex flex-col sm:flex-row gap-5 sm:gap-10 w-full h-[calc(100vh-8rem)]">

      <div class="hidden sm:block w-full sm:w-2/12 p-1">
        <div>
          <UInput
            icon="i-lucide-search"
            size="lg"
            v-model="filterValue"
            :placeholder="`${ t('buttons.buscar') }...`"
          />
        </div>
        <div class="overflow-scroll h-full mt-2">
          <div class="list-none mb-10">
            <ul>
                <li v-for="country in filteredCountries" 
                  @click="getCountryInfo(country)" 
                  :class="['flex gap-1 items-center my-2 mr-2 px-2 py-2 rounded-md hover:cursor-pointer', { 'hover:bg-neutral-50': countryName !== country.name }, { 'bg-neutral-100 hover:bg-neutral-100': countryName === country.name }]"
                >
                {{ country.name }}
                <UIcon v-if="countryName === country.name" name="i-heroicons-map-pin-solid" class="-mt-0.5" />
                </li>
              <li v-if="!filteredCountries.length" class="my-1 mr-2 px-2 py-2 rounded-md text-neutral-500">
                {{ t('labels.errorDestino') }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      <!-- Selector -->
      <div class="sm:hidden">
        <USelectMenu
          :model-value="currentCountry as any"
          @update:model-value="(value: any) => currentCountry = value"
          :items="countries as any"
          labelKey="name"
          :search-input="{
            placeholder: `${t('buttons.buscar')}...`,
          }"
          :placeholder="t('labels.selectDestino')"
          size="xl"
        >
          <template #empty>
            {{ t('buttons.noDispo') }}
          </template>
        </USelectMenu>
      </div>
      
      <!-- Mapa -->
      <div class="w-full h-full sm:w-10/12 z-0 pb-10">
        <div v-if="citiesStatus === 'pending'" class="w-full h-full" >
          <USkeleton class="h-full w-full rounded-3xl" />
        </div>
        <DestinationMap v-else-if="countryId" :key="mapKey" :data="mapCities" :bound="latlonCities" :navigate="navigate" />
        <div v-else class="relative w-full h-full">
          <div class="bg-neutral-50/70 backdrop-blur-sm absolute z-10 w-full h-full flex items-center justify-center rounded-3xl">
            <div class="text-center text-xl text-neutral-500 font-bold">
              <UIcon name="i-heroicons-map-pin-solid"/>
              <p>{{ t('labels.selectDestino') }}</p>
            </div>
          </div>
          <DestinationMap :data="[]" />
        </div>
      </div>
    </div>
  </div>
</template>